import { Component, createSignal } from 'solid-js';
import { TbCalendar } from 'solid-icons/tb';
import moment from 'moment';
import { getTodayGMT, getYesterdayGMT, getDateRangeGMT } from '../../utils/timezone';

interface DateRangePickerProps {
  onDateChange: (startDate: string, endDate: string) => void;
  defaultRange?: 'today' | 'week' | 'month' | 'year';
  initialStartDate?: string;
  initialEndDate?: string;
}

/**
 * DateRangePicker Component
 * Simple date range selector with presets
 */
const DateRangePicker: Component<DateRangePickerProps> = (props) => {
  const [startDate, setStartDate] = createSignal(
    props.initialStartDate || getDateRangeGMT(7).startDate
  );
  const [endDate, setEndDate] = createSignal(
    props.initialEndDate || getTodayGMT()
  );

  const handleStartChange = (value: string) => {
    setStartDate(value);
    props.onDateChange(value, endDate());
  };

  const handleEndChange = (value: string) => {
    setEndDate(value);
    props.onDateChange(startDate(), value);
  };

  const applyPreset = (preset: 'today' | 'yesterday' | 'week' | 'month' | 'year' | 'thisWeek' | 'lastWeek' | 'twoWeeksAgo') => {
    let start: string;
    let end: string;

    switch (preset) {
      case 'today':
        start = getTodayGMT();
        end = getTodayGMT();
        break;
      case 'yesterday':
        start = getYesterdayGMT();
        end = getYesterdayGMT();
        break;
      case 'week':
        const weekRange = getDateRangeGMT(7);
        start = weekRange.startDate;
        end = weekRange.endDate;
        break;
      case 'month':
        const monthRange = getDateRangeGMT(30);
        start = monthRange.startDate;
        end = monthRange.endDate;
        break;
      case 'year':
        const yearRange = getDateRangeGMT(365);
        start = yearRange.startDate;
        end = yearRange.endDate;
        break;
      case 'thisWeek':
        // This Monday to this Sunday (current week)
        start = moment.utc().startOf('isoWeek').format('YYYY-MM-DD');
        end = moment.utc().endOf('isoWeek').format('YYYY-MM-DD');
        break;
      case 'lastWeek':
        // Last Monday to Sunday (7 days)
        start = moment.utc().startOf('isoWeek').subtract(7, 'days').format('YYYY-MM-DD');
        end = moment.utc().startOf('isoWeek').subtract(1, 'day').format('YYYY-MM-DD');
        break;
      case 'twoWeeksAgo':
        // 2 weeks ago Monday to Sunday (7 days)
        start = moment.utc().startOf('isoWeek').subtract(14, 'days').format('YYYY-MM-DD');
        end = moment.utc().startOf('isoWeek').subtract(8, 'days').format('YYYY-MM-DD');
        break;
    }

    setStartDate(start);
    setEndDate(end);
    props.onDateChange(start, end);
  };

  return (
    <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div class="flex items-center gap-2 mb-3">
        <TbCalendar size={20} class="text-gray-500 dark:text-gray-400" />
        <h3 class="text-sm font-medium text-gray-900 dark:text-white">Date Range</h3>
      </div>

      {/* Date Inputs */}
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">From</label>
          <input
            type="date"
            value={startDate()}
            onChange={(e) => handleStartChange(e.currentTarget.value)}
            class="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">To</label>
          <input
            type="date"
            value={endDate()}
            onChange={(e) => handleEndChange(e.currentTarget.value)}
            class="block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Preset Buttons - Quick date range filters */}
      <div class="flex flex-wrap gap-2">
        {/* Today button - single day view */}
        <button
          onClick={() => applyPreset('today')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Today
        </button>

        {/* Yesterday button - previous day view */}
        <button
          onClick={() => applyPreset('yesterday')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Yesterday
        </button>

        {/* Last 7 Days button - weekly view */}
        <button
          onClick={() => applyPreset('week')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Last 7 Days
        </button>

        {/* Last 30 Days button - HIDDEN
            Reason: Not needed, users can use date picker manually or use Last 7 Days preset
            To enable: Remove display:none class */}
        <button
          onClick={() => applyPreset('month')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors hidden"
        >
          Last 30 Days
        </button>

        {/* Last Year button - HIDDEN
            Reason: Large time period rarely needed for daily analytics, can use custom date range instead
            To enable: Remove hidden class */}
        <button
          onClick={() => applyPreset('year')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors hidden"
        >
          Last Year
        </button>

        {/* This Week button - current calendar week */}
        <button
          onClick={() => applyPreset('thisWeek')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          This Week
        </button>

        {/* Last Week button - previous calendar week */}
        <button
          onClick={() => applyPreset('lastWeek')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
        >
          Last Week
        </button>

        {/* 2 Weeks Ago button - HIDDEN
            Reason: Less commonly used, week-by-week comparison available via Last Week button
            To enable: Remove hidden class */}
        <button
          onClick={() => applyPreset('twoWeeksAgo')}
          class="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors hidden"
        >
          2 Weeks Ago
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;
